import React from "react";
import styled from "styled-components/macro";
import { Avatar as EDSAvatar } from "@chanzuckerberg/eds";
import { getBeautyMarkColor } from "helpers/getBeautyMark";
import { theme } from "ui/theme";

export enum AvatarState {
  PLAYING = "playing",
  UNREAD = "unread",
  READ = "read",
  NO_REFLECTION = "noReflection",
  REPLIED = "replied",
}

export const AvatarSize = {
  sm: "var(--eds-size-4)",
  md: "var(--eds-size-5)",
  lg: "var(--eds-size-6)",
  xl: "var(--eds-size-12)",
  xxl: "12rem",
};

const $AvatarWithBackground = styled(EDSAvatar)<{
  $userId: string;
  $state?: AvatarState;
  size: "sm" | "md" | "lg";
  $alongSize: "sm" | "md" | "lg" | "xl" | "xxl";
}>`
  ${({ $userId }) => {
    const color = getBeautyMarkColor($userId);
    return `
      background-color: ${color.backgroundColor};
      color: ${color.color};
    `;
  }};

  ${({ size }) => size === "sm" && "font-size: 1rem;"}

  ${({ $alongSize }) => {
    switch ($alongSize) {
      case "xl":
        return `
          width: ${AvatarSize.xl};
          height: ${AvatarSize.xl};
          font: var(--eds-theme-typography-title-lg);
          font-size: 2.75rem;
        `;
      case "xxl":
        return `
          width: ${AvatarSize.xxl};
          height: ${AvatarSize.xxl};
          font: var(--eds-theme-typography-title-lg);
          font-size: 5rem;
        `;
      default:
        return;
    }
  }}

  // Don't resize if the avatar is a flex child.
  flex: 0 0 auto;

  img {
    width: 100%;
  }

  ${({ $state }) =>
    $state === AvatarState.UNREAD &&
    `
      &::after {
        content: '';
        width: calc(${AvatarSize.sm} + 8px);
        height: calc(${AvatarSize.sm} + 8px);
        background: transparent;
        border-radius: 50%;
        position: absolute;
        border: 2px solid ${theme.ColorMint500};
      }
    `}
`;

interface AvatarProps
  extends Omit<
    React.ComponentProps<typeof EDSAvatar>,
    "src" | "user" | "variant" | "size"
  > {
  userId: string;
  name: string;
  profileImage?: string;
  state?: AvatarState;
  size?: "sm" | "md" | "lg" | "xl" | "xxl";
}

export default function Avatar({
  profileImage,
  userId,
  name,
  size = "sm",
  shape = "circle",
  state,
  ...props
}: AvatarProps) {
  // removes substrings within parentheses
  const filteredName = name.replace(/\([^)]*\)/g, "");
  return (
    <$AvatarWithBackground
      size={size === "xl" || size === "xxl" ? "lg" : size}
      $alongSize={size}
      shape={shape}
      variant={profileImage ? "image" : "initials"}
      src={profileImage}
      $userId={userId}
      user={{
        fullName: filteredName,
        id: userId,
      }}
      $state={state}
      {...props}
    />
  );
}
